'use client'

import React, { useCallback, useEffect, useRef } from 'react'
import type { TeamMembersBlock as TeamMembersBlockProps } from '@/payload-types'
import { cn } from '@/utilities/ui'
import RichText from '@/components/RichText'
import useEmblaCarousel from 'embla-carousel-react'
import {
  Wrench,
  Gamepad2,
  Settings,
  Code,
  Tractor,
  Briefcase,
  GraduationCap,
  Hammer,
  TrendingUp,
  Flame,
  Building2,
  Package,
  HardHat,
  Plane,
  Zap,
  Truck,
  Heart,
  Code2,
  Radio,
  Car,
  Video,
  Palette,
  ChefHat,
  Scale,
  Calculator,
  Languages,
  Cpu,
  Antenna,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

type TeamRole = {
  name: string
  nameEn: string
  icon: LucideIcon
}

const TEAM_ROLES: TeamRole[] = [
  { name: 'Інженери', nameEn: 'Engineers', icon: Wrench },
  { name: 'Геймери', nameEn: 'Gamers', icon: Gamepad2 },
  { name: 'Механіки', nameEn: 'Mechanics', icon: Settings },
  { name: 'IT-фахівці', nameEn: 'IT specialists', icon: Code },
  { name: 'Фермери', nameEn: 'Farmers', icon: Tractor },
  { name: 'Менеджери', nameEn: 'Managers', icon: Briefcase },
  { name: 'Вчителі', nameEn: 'Teachers', icon: GraduationCap },
  { name: 'Слюсарі', nameEn: 'Locksmiths', icon: Hammer },
  { name: 'Маркетологи', nameEn: 'Marketers', icon: TrendingUp },
  { name: 'Зварники', nameEn: 'Welders', icon: Flame },
  { name: 'Бізнесмени', nameEn: 'Businessmen', icon: Building2 },
  { name: 'Логісти', nameEn: 'Logisticians', icon: Package },
  { name: 'Будівельники', nameEn: 'Builders', icon: HardHat },
  { name: 'Оператори дронів', nameEn: 'Drone operators', icon: Plane },
  { name: 'Електрики', nameEn: 'Electricians', icon: Zap },
  { name: 'Водії', nameEn: 'Drivers', icon: Truck },
  { name: 'Медики', nameEn: 'Medics', icon: Heart },
  { name: 'Програмісти', nameEn: 'Programmers', icon: Code2 },
  { name: 'Радіотехніки', nameEn: 'Radio technicians', icon: Radio },
  { name: 'Автомеханіки', nameEn: 'Auto mechanics', icon: Car },
  { name: 'Відеографи', nameEn: 'Videographers', icon: Video },
  { name: 'Дизайнери', nameEn: 'Designers', icon: Palette },
  { name: 'Кухарі', nameEn: 'Cooks', icon: ChefHat },
  { name: 'Юристи', nameEn: 'Lawyers', icon: Scale },
  { name: 'Бухгалтери', nameEn: 'Accountants', icon: Calculator },
  { name: 'Перекладачі', nameEn: 'Translators', icon: Languages },
  { name: 'Електронщики', nameEn: 'Electronics engineers', icon: Cpu },
  { name: "Зв'язківці", nameEn: 'Signallers', icon: Antenna },
]

type Props = {
  className?: string
  locale?: string
} & TeamMembersBlockProps

export const TeamMembersBlock: React.FC<Props> = ({ className, title, description, locale }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    dragFree: true,
    containScroll: false,
  })

  const scrollIntervalRef = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      emblaRef(node)
      containerRef.current = node
    },
    [emblaRef],
  )

  useEffect(() => {
    if (!emblaApi) return

    const scrollSpeed = 7 // pixels per interval
    const scrollInterval = 16 // milliseconds (approximately 60fps)

    const autoScroll = () => {
      const engine = emblaApi.internalEngine()
      const { scrollTo, target } = engine

      // Scroll to the left
      scrollTo.distance(-scrollSpeed, false)
      target.set(engine.location.get() - scrollSpeed)
    }

    // Start auto-scrolling
    scrollIntervalRef.current = window.setInterval(autoScroll, scrollInterval)

    // Pause on mouse enter
    const emblaNode = containerRef.current
    if (emblaNode) {
      const handleMouseEnter = () => {
        if (scrollIntervalRef.current !== null) {
          clearInterval(scrollIntervalRef.current)
          scrollIntervalRef.current = null
        }
      }

      const handleMouseLeave = () => {
        if (scrollIntervalRef.current === null) {
          scrollIntervalRef.current = window.setInterval(autoScroll, scrollInterval)
        }
      }

      emblaNode.addEventListener('mouseenter', handleMouseEnter)
      emblaNode.addEventListener('mouseleave', handleMouseLeave)

      return () => {
        if (scrollIntervalRef.current !== null) {
          clearInterval(scrollIntervalRef.current)
        }
        emblaNode.removeEventListener('mouseenter', handleMouseEnter)
        emblaNode.removeEventListener('mouseleave', handleMouseLeave)
      }
    }

    return () => {
      if (scrollIntervalRef.current !== null) {
        clearInterval(scrollIntervalRef.current)
      }
    }
  }, [emblaApi])

  return (
    <div className={cn('my-8 md:my-16', className)}>
      <div className="container">
        <div className="text-center mb-4 md:mb-6">
          {title && (
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4">{title}</h2>
          )}
          {description && (
            <div className="max-w-3xl mx-auto text-base md:text-lg text-muted-foreground">
              <RichText data={description} enableGutter={false} />
            </div>
          )}
        </div>
      </div>

      {/* Full-width scrolling banner with Embla */}
      <div className="relative overflow-hidden">
        {/* Gradient overlays for fade effect */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        {/* Embla carousel container */}
        <div ref={setRefs} className="overflow-hidden">
          <div className="flex gap-2 md:gap-3">
            {/* Render roles multiple times for seamless loop */}
            {[...Array(5)].map((_, setIndex) =>
              TEAM_ROLES.map((role, roleIndex) => {
                const Icon = role.icon
                const displayName = locale === 'en' ? role.nameEn : role.name
                return (
                  <div
                    key={`${setIndex}-${roleIndex}`}
                    className="group inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full border border-border bg-card hover:bg-primary hover:border-primary transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <Icon className="w-3 h-3 md:w-4 md:h-4 text-primary group-hover:text-white transition-colors" />
                    <span className="text-xs md:text-sm font-medium group-hover:text-white transition-colors">
                      {displayName}
                    </span>
                  </div>
                )
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
